import { StoresList } from '@/models/enums/app/StoresList'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import type { Attribut } from '@/models/interfaces/Attribut'
import type { CompetenceAttribut } from '@/models/interfaces/CompetenceAttribut'
import { AttributService } from '@/services/attribut/AttributService'

export const useAttributStore = defineStore(StoresList.ATTRIBUT, () => {
  const attributList: Ref<Attribut[]> = ref([])
  const competenceAttributList: Ref<CompetenceAttribut[]> = ref([])
  const indicateurPcdList: Ref<Attribut[]> = ref([])

  async function getAll(): Promise<void> {
    if (attributList.value.length === 0 || competenceAttributList.value.length === 0) {
      const [attributs, competenceAttributs] = await Promise.all([
        AttributService.getAll(),
        AttributService.getCompetenceAttributs(),
      ])
      attributList.value = attributs
      competenceAttributList.value = competenceAttributs
    }
  }

  function getIndicateursByCompetenceId(cptId: number): Attribut[] {
    const attributIds = competenceAttributList.value
      .filter((ca) => ca.fkCptId === cptId)
      .map((ca) => ca.fkAttributId)

    return attributList.value.filter(
      (attribut) => attributIds.includes(attribut.atgId) && attribut.atgKind === 'I',
    )
  }

  /**
   * Indicateurs of the PCD classification (direct attribut -> sousdomaine link, commune scope).
   * Only the 53 indicateurs of Classement_des_indicateurs_suivant_le_PCD have fkSousdomaineId set;
   * every other attribut is excluded, per the backend's Version20260904190000 migration. Fetched
   * via the server-side atgKind/atgGeographicScope filter (GET /api/attributs ApiFilter) instead
   * of loading the full attribut list, so this is independent from getAll()/attributList.
   */
  async function getIndicateursPcd(): Promise<void> {
    if (indicateurPcdList.value.length === 0) {
      indicateurPcdList.value = await AttributService.getIndicateursPcd()
    }
  }

  function getIndicateursBySousdomaineId(sousDomaineId: number): Attribut[] {
    return indicateurPcdList.value.filter((attribut) => attribut.fkSousdomaineId === sousDomaineId)
  }

  return {
    attributList,
    competenceAttributList,
    indicateurPcdList,
    getAll,
    getIndicateursByCompetenceId,
    getIndicateursPcd,
    getIndicateursBySousdomaineId,
  }
})
